// Builds the printable consent record opened by the admin's print/export
// buttons. Extracted out of ConsentExport.js so one document can carry SEVERAL
// applicants: printing a joint application used to produce a document that gave
// no indication which applicant it belonged to, so two prints looked identical.
// Each applicant now gets its own clearly labelled, page-broken block.

export const escapeHtml = (str) =>
    String(str == null ? '' : str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

export const safeDateTime = (value) => {
    if (!value) return 'N/A';
    const d = new Date(value);
    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleString('en-AU');
};

const isoDatePart = (value) => {
    const d = value ? new Date(value) : null;
    if (!d || isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// The document <title> becomes the default filename under "Save as PDF".
const buildFileTitle = (entries, meta) => {
    const parts = ['Privacy Consent Record'];

    if (entries.length === 1) {
        if (entries[0].user?.name) parts.push(entries[0].user.name);
    } else {
        parts.push(`${entries.length} Applicants`);
    }

    const applicationId = entries.map((e) => e.user?.application_id).find(Boolean);
    if (applicationId) parts.push(applicationId);

    const dateSource =
        (entries.length === 1 && entries[0].version && entries[0].version.submitted_at) || meta.exported_at;
    const datePart = isoDatePart(dateSource);
    if (datePart) parts.push(datePart);

    return parts.join(' - ').replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim();
};

const renderItemHtml = (item) => {
    const isCredit = item.type === 'credit_check';
    const statusHtml = isCredit
        ? `<span class="${item.answer === 'yes' ? 'status' : 'status-bad'}">Credit check: ${item.answer ? item.answer.toUpperCase() : 'N/A'}</span>`
        : `<span class="${item.acknowledged ? 'status' : 'status-bad'}">${item.acknowledged ? 'Acknowledged' : 'Not acknowledged'}</span>`;

    let body = '';
    // item.content is trusted, server-provided registry content.
    if (item.content) body += `<div class="policy-content">${item.content}</div>`;
    if (item.url) {
        body += `<p><strong>Policy link:</strong> <a href="${escapeHtml(item.url)}">${escapeHtml(item.url)}</a></p>`;
        body += `<p class="meta">This document is hosted externally; the customer acknowledged the version live at the link above on the consent date.</p>`;
    }

    return `
      <div class="section">
        <h3>${escapeHtml(item.title)} <span class="meta">(version ${escapeHtml(item.version)})</span></h3>
        <p><strong>Status:</strong> ${statusHtml}</p>
        ${body}
      </div>
    `;
};

// One applicant's block: who they are, which snapshot, and every consent item.
const renderApplicantHtml = (entry, index, total) => {
    const user = entry.user || {};
    const version = entry.version;
    const heading =
        total > 1
            ? `Applicant ${index + 1} of ${total}: ${escapeHtml(user.name || 'Unknown Client')}`
            : `Client Information`;

    if (!version) {
        return `
      <div class="applicant">
        <h2 class="applicant-heading">${heading}</h2>
        <div class="section">
          <table>
            <tr><th>Name</th><td>${escapeHtml(user.name || 'N/A')}</td></tr>
            <tr><th>Email</th><td>${escapeHtml(user.email || 'N/A')}</td></tr>
            <tr><th>Application ID</th><td>${escapeHtml(user.application_id || 'N/A')}</td></tr>
          </table>
          <p><strong>No consent has been recorded for this client.</strong></p>
        </div>
      </div>
    `;
    }

    const submittedBy = version.submitted_by || {};
    const itemsHtml = (version.items || []).map(renderItemHtml).join('');

    return `
      <div class="applicant">
        <h2 class="applicant-heading">${heading}</h2>

        <div class="section">
          <h3>Client Information</h3>
          <table>
            <tr><th>Name</th><td>${escapeHtml(user.name || 'N/A')}</td></tr>
            <tr><th>Email</th><td>${escapeHtml(user.email || 'N/A')}</td></tr>
            <tr><th>Application ID</th><td>${escapeHtml(user.application_id || 'N/A')}</td></tr>
          </table>
        </div>

        <div class="section">
          <h3>Consent Version</h3>
          <table>
            <tr><th>Version ID</th><td style="word-break: break-all;">${escapeHtml(version.version_id)}</td></tr>
            <tr><th>Submitted At</th><td>${safeDateTime(version.submitted_at)}</td></tr>
            <tr><th>Expires</th><td>${safeDateTime(version.expires_at)}</td></tr>
            <tr><th>Submitted By</th><td>${escapeHtml(submittedBy.name || 'N/A')} (${escapeHtml(submittedBy.email || 'N/A')})</td></tr>
            <tr><th>IP Address</th><td>${escapeHtml(version.ip_address || 'N/A')}</td></tr>
            <tr><th>User Agent</th><td style="word-break: break-all;">${escapeHtml(version.user_agent || 'N/A')}</td></tr>
            <tr><th>Snapshots On Record</th><td>${escapeHtml(entry.versions_count)}</td></tr>
          </table>
        </div>

        ${itemsHtml}
      </div>
    `;
};

const DOCUMENT_STYLES = `
    body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; color: #333; }
    .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
    .applicant { margin-bottom: 40px; }
    /* Start each applicant on a fresh sheet so the two consents never blur together. */
    .applicant + .applicant { page-break-before: always; }
    .applicant-heading { font-size: 18px; color: #1e2129; background: #EFEAFE; border-left: 5px solid #5B3FE0;
      padding: 10px 14px; border-radius: 4px; margin: 0 0 18px; page-break-after: avoid; }
    .section { margin-bottom: 25px; padding: 15px; border: 1px solid #ddd; border-radius: 5px; page-break-inside: avoid; }
    .section h3 { margin-top: 0; color: #2c5aa0; border-bottom: 1px solid #eee; padding-bottom: 10px; }
    .policy-content { background:#f8f9fa; padding: 12px 16px; border-radius: 5px; margin: 10px 0; font-size: 13px; }
    .policy-content h4 { margin: 12px 0 4px; color:#1e2129; font-size: 14px; }
    .warning { border: 2px solid #dc3545; background: #fdf0f1; color: #8b1a23; padding: 12px 16px;
      border-radius: 5px; margin-bottom: 25px; font-size: 13px; page-break-inside: avoid; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; font-size: 12px; color: #666; }
    .status { font-weight: bold; color: #28a745; }
    .status-bad { font-weight: bold; color: #dc3545; }
    .meta { color:#888; font-weight: normal; font-size: 12px; }
    table { width: 100%; border-collapse: collapse; margin: 10px 0; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #f2f2f2; width: 30%; }
    @media print { body { margin: 20px; } .no-print { display: none; } }
`;

/**
 * Open a printable consent record in a new window.
 *
 * @param {Array}  entries One per applicant: { user, version, versions_count }.
 * @param {object} meta    { exported_at, exported_by }.
 */
export const generateConsentDocument = (entries, meta = {}) => {
    const list = Array.isArray(entries) ? entries : [entries];
    const pdfWindow = window.open('', '_blank');
    if (!pdfWindow) return; // popup blocked

    const fileTitle = buildFileTitle(list, meta);

    if (list.length === 0) {
        pdfWindow.document.write(`
        <html><head><title>${escapeHtml(fileTitle)}</title></head>
        <body style="font-family: Arial, sans-serif; margin: 40px;">
          <h1>Privacy Consent Record</h1>
          <p>No clients were found for this application.</p>
        </body></html>
      `);
        pdfWindow.document.close();
        return;
    }

    const subtitle =
        list.length > 1
            ? `<p>${list.length} applicants, each recorded separately below.</p>`
            : '';

    // Two applicants resolving to the same consent snapshot means they share a
    // login (usually one email across both). Say so loudly rather than printing
    // one person's consent under someone else's name.
    const versionIds = list.map((e) => e.version && e.version.version_id).filter(Boolean);
    const hasDuplicateVersion = new Set(versionIds).size !== versionIds.length;
    const duplicateWarning = hasDuplicateVersion
        ? `<div class="warning">
             <strong>Warning:</strong> more than one applicant below resolves to the same
             consent record, so this document does not evidence a separate consent per person.
             That happens when the applicants share a single login or email address. Give each
             applicant their own email address and have them consent individually before
             relying on this record.
           </div>`
        : '';

    const pdfContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${escapeHtml(fileTitle)}</title>
          <style>${DOCUMENT_STYLES}</style>
        </head>
        <body>
          <div class="header">
            <h1>Privacy Consent Record</h1>
            ${subtitle}
            <p>Generated on ${new Date().toLocaleDateString('en-AU')}</p>
          </div>

          ${duplicateWarning}

          ${list.map((entry, i) => renderApplicantHtml(entry, i, list.length)).join('')}

          <div class="section">
            <h3>Export Information</h3>
            <table>
              <tr><th>Exported At</th><td>${safeDateTime(meta.exported_at)}</td></tr>
              <tr><th>Exported By</th><td>${escapeHtml(meta.exported_by || 'N/A')}</td></tr>
              <tr><th>Applicants In This Document</th><td>${list.length}</td></tr>
            </table>
          </div>

          <div class="footer">
            <p>This document contains confidential information and should be handled according to privacy regulations.</p>
            <p>LoanGateway - CBD Mortgage Advisors</p>
          </div>

          <div class="no-print" style="margin-top: 30px; text-align: center;">
            <button onclick="window.print()" style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 5px; cursor: pointer; margin-right: 10px;">Print PDF</button>
            <button onclick="window.close()" style="padding: 10px 20px; background: #6c757d; color: white; border: none; border-radius: 5px; cursor: pointer;">Close</button>
          </div>
        </body>
      </html>
    `;

    pdfWindow.document.write(pdfContent);
    pdfWindow.document.close();
};
